import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantSalesAgentCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type {
  CreateMerchantSalesAgentPayload,
  MerchantSalesAgentDto,
} from "@/features/merchant/api/dto";

/**
 * Crée un agent de vente pour un marchand
 * (`POST /MerchantSalesAgents/merchant/{merchantId}/add?actingMerchantId=...`).
 * Rôle : merchant avec permission `CREATE_SALES_AGENT` ou back-office.
 */
export const useCreateMerchantSalesAgent = (merchantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "sales-agents", "create", merchantId],
    mutationFn: ({
      body,
      actingMerchantId,
    }: {
      body: CreateMerchantSalesAgentPayload;
      actingMerchantId?: string;
    }) => {
      const query = actingMerchantId
        ? `?actingMerchantId=${actingMerchantId}`
        : "";
      return mutateData({
        url: `${merchantEndpoints.merchantSalesAgents.add(merchantId)}${query}`,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<MerchantSalesAgentDto>;
    },
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        merchantSalesAgentCacheKeys(merchantId),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateMerchantSalesAgent failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
